import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Sparkles, Clock, CheckCircle2, ChevronDown, ChevronUp, Lock, Hash, AlertTriangle } from 'lucide-react';
import { PatientRecommendations, RecommendationItem } from "../../../patient";
import { getRecommendations, type RecommendationsSubject } from "../../application/sessionRecommendations";
import { useSubscription } from "../../../subscription";
import Button from '../../../../shared/ui/button/Button';
import { AiBadge } from '../../../../shared/ui/primitives';
import ClinicalAlertsHistory from './ClinicalAlertsHistory';
import { useAiInsightsGate, LockedState } from "../../../ai_insights";

interface RecommendationsPanelProps {
    patientId: string;
    isCouple?: boolean;
    /** Whose these are; wins over `isCouple`. A group's are read from its own sessions. */
    subjectKind?: RecommendationsSubject;
    refreshKey?: number;
    className?: string;
}

const PAGE: Record<RecommendationsSubject, string> = {
    patient: '/patient',
    couple: '/couple-patient',
    group: '/group-patient',
};

const RecommendationsPanel: React.FC<RecommendationsPanelProps> = ({ patientId, isCouple, subjectKind, refreshKey = 0, className }) => {
    const kind: RecommendationsSubject = subjectKind ?? (isCouple ? 'couple' : 'patient');
    // The group page has no recommendations tab of its own; its main page is where "more" goes.
    const moreLink = kind === 'group' ? `${PAGE.group}/${patientId}` : `${PAGE[kind]}/${patientId}?tab=recommendations`;
    const { t } = useTranslation();
    const { isPremium } = useSubscription();
    const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
    const [recommendations, setRecommendations] = useState<PatientRecommendations | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchRecommendations = async () => {
            // Recommendations are part of the AI Insights tool: don't call the API
            // (it would 403) until adoption is confirmed.
            if (!isPremium || adoptionLoading || !aiInsightsAdopted) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const data = await getRecommendations(patientId, kind);
                setRecommendations(data);
            } catch (err) {
                console.error('Failed to load recommendations:', err);
            } finally {
                setLoading(false);
            }
        };

        if (patientId) {
            fetchRecommendations();
        }
    }, [patientId, kind, isPremium, refreshKey, aiInsightsAdopted, adoptionLoading]);

    if (!isPremium) {
        return (
            <div className={`h-full ${className || ''}`}>
                <div className="bg-surface-raised rounded-2xl shadow-raised border border-border-subtle p-6 text-center">
                    <div className="w-12 h-12 bg-surface-sunken rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock className="w-6 h-6 text-content-tertiary" />
                    </div>
                    <h4 className="text-sm font-bold text-content-primary mb-2">{t('recommendations.title')}</h4>
                    <p className="text-xs text-content-secondary mb-4 px-2">
                        {t('subscription.restricted.description')}
                    </p>
                    <Button
                        size="sm"
                        onClick={() => window.location.href = '/subscription'}
                        className="w-full text-xs py-2 ai-spectrum-fill"
                    >
                        {t('subscription.status.upgrade')}
                    </Button>
                </div>
            </div>
        );
    }

    if (!adoptionLoading && !aiInsightsAdopted) {
        return (
            <div className={`h-full ${className || ''}`}>
                <LockedState title={t('aiInsights.locked.recommendations')} />
            </div>
        );
    }

    if (loading) return (
        <div className={`h-full ${className || ''}`}>
            <div className="bg-surface-raised rounded-2xl shadow-raised border border-border-subtle p-8 space-y-4">
                <div className="h-4 bg-surface-sunken rounded w-1/2 animate-pulse" />
                <div className="h-20 bg-surface-sunken rounded animate-pulse" />
                <div className="h-32 bg-surface-sunken rounded animate-pulse" />
            </div>
        </div>
    );

    if (!recommendations) return null;

    return (
        <div className={`shrink-0 sticky top-6 ${className || ''}`}>
            <div className="ai-spectrum-rule flex h-full flex-col overflow-hidden rounded-xl border border-border-subtle bg-surface-raised">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle p-4">
                    <h3 className="text-sm font-semibold text-content-primary">
                        {t('recommendations.title')}
                    </h3>
                    {/* These are model output. The rule above the panel and this
                        badge say so in colour and in words respectively. */}
                    <AiBadge label={t('aiInsights.generatedBadge')} />
                </div>

                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-6 pb-20">
                    {/* Clinical alerts are kept per patient and per couple; a group has none yet. */}
                    {kind !== 'group' && (
                    <section>
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                            <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">{t("recommendations.recentAlerts", "Recent Clinical Alerts")}</h4>
                        </div>
                        <ClinicalAlertsHistory
                            patientId={patientId}
                            isCouple={isCouple}
                            limit={3}
                            title={null}
                            compact={true}
                            variant="list"
                            refreshKey={refreshKey}
                            showMoreLink={isCouple ? `/couple-patient/${patientId}?tab=alerts` : `/patient/${patientId}?tab=alerts`}
                        />
                    </section>
                    )}

                    <section>
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-3.5 h-3.5 text-accent-text" />
                            <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">{t('recommendations.lastSessionSummary')}</h4>
                        </div>
                        <p className="text-xs text-accent-on-tint bg-accent-tint p-3 rounded-lg border border-accent/20 italic leading-relaxed">
                            "{recommendations.last_session_summary}"
                        </p>
                    </section>

                    <section>
                        <div className="flex items-center gap-2 mb-3">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                            <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">{t('recommendations.previousAgreements')}</h4>
                        </div>
                        <div className="space-y-2">
                            {recommendations.previous_agreements.slice(0, 3).map((item, i) => (
                                <ExpandableItem key={i} item={item} colorClass="bg-surface-sunken text-content-secondary border-border-subtle" />
                            ))}
                            {recommendations.previous_agreements.length === 0 && (
                                <p className="px-1 text-xs text-content-secondary">{t('recommendations.noAgreements')}</p>
                            )}
                            {recommendations.previous_agreements.length > 3 && (
                                <div className="px-1 pt-1">
                                    <Link
                                        to={moreLink}
                                        className="text-xs font-medium text-accent-text hover:text-accent-hover hover:underline"
                                    >
                                        {t("common.showMore", "Show more")}...
                                    </Link>
                                </div>
                            )}
                        </div>
                    </section>

                    <section>
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-3.5 h-3.5 text-accent-text" />
                            <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">{t('recommendations.suggestedTechniques')}</h4>
                        </div>
                        <div className="space-y-2">
                            {recommendations.suggested_techniques.slice(0, 3).map((item, i) => (
                                <ExpandableItem key={i} item={item} colorClass="bg-surface-sunken text-content-secondary border-border-subtle" />
                            ))}
                            {recommendations.suggested_techniques.length === 0 && (
                                <p className="px-1 text-xs text-content-secondary">{t('recommendations.noneSuggested')}</p>
                            )}
                            {recommendations.suggested_techniques.length > 3 && (
                                <div className="px-1 pt-1">
                                    <Link
                                        to={moreLink}
                                        className="text-xs font-medium text-accent-text hover:text-accent-hover hover:underline"
                                    >
                                        {t("common.showMore", "Show more")}...
                                    </Link>
                                </div>
                            )}
                        </div>
                    </section>

                    <section>
                        <div className="flex items-center gap-2 mb-3">
                            <Hash className="w-3.5 h-3.5 text-orange-500" />
                            <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">{t('recommendations.recurringThemes')}</h4>
                        </div>
                        <div className="space-y-2">
                            {recommendations.recurring_topics.slice(0, 3).map((item, i) => (
                                <ExpandableItem
                                    key={i}
                                    item={item}
                                    colorClass="bg-surface-sunken text-content-secondary border-border-subtle"
                                />
                            ))}
                            {recommendations.recurring_topics.length === 0 && (
                                <p className="px-1 text-xs text-content-secondary">{t('recommendations.noneRecorded')}</p>
                            )}
                            {recommendations.recurring_topics.length > 3 && (
                                <div className="px-1 pt-1">
                                    <Link
                                        to={moreLink}
                                        className="text-xs font-medium text-accent-text hover:text-accent-hover hover:underline"
                                    >
                                        {t("common.showMore", "Show more")}...
                                    </Link>
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                <div className="border-t border-border-subtle bg-surface-sunken p-3">
                    <p className="text-right text-xs text-content-secondary">
                        {t('recommendations.analysisBasedOn')} ({recommendations.last_session_date ? new Date(recommendations.last_session_date).toLocaleDateString() : 'N/A'})
                    </p>
                </div>
            </div>
        </div>
    );
};

const ExpandableItem: React.FC<{ item: RecommendationItem, colorClass: string, icon?: React.ReactNode }> = ({ item, colorClass, icon }) => {
    const [expanded, setExpanded] = useState(false);


    return (
        <div className={`rounded-lg border transition-all ${colorClass}`}>
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-2.5 text-left cursor-pointer transition-colors duration-[--duration-fast] hover:bg-surface-sunken rounded-lg"
            >
                <div className="flex items-center gap-2.5">
                    {icon && <div className="p-1 rounded-md bg-surface-raised shadow-raised">{icon}</div>}
                    <span className="text-xs font-medium leading-tight">{item.title || item.content.slice(0, 50) + (item.content.length > 50 ? '...' : '')}</span>
                </div>
                <div className="text-current opacity-50">
                    {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </div>
            </button>
            {expanded && (
                <div className="px-3 pb-2.5 pt-0.5 text-xs leading-relaxed opacity-80 border-t border-current/5 mt-0.5">
                    {item.content}
                </div>
            )}
        </div>
    );
};

export default RecommendationsPanel;
